import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MathHelper } from '@core/helpers/math.helper';
import { TreeNodeHelper } from '@core/helpers/tree-node.helper';
import { Utils } from '@core/helpers/util-functions';
import { EstimateTypeEnum } from '@enums/EstimateType';
import { StatusEnum } from '@enums/Status';
import { OverBudgetError, TreeNodeData, TreeNodeType } from '@estimate/treetable/models/tree-node-data.model';
import { BudgetDto } from '@interfaces/BudgetDto';
import { BudgetValueDto } from '@interfaces/BudgetValueDto';
import { ClassificationEstimate } from '@interfaces/ClassificationEstimate';
import { Estimate } from '@interfaces/Estimate';
import { EstimateLine } from '@interfaces/EstimateLine';
import { InternalResource_Estimate } from '@interfaces/InternalResource_Estimate';
import { Structure } from '@interfaces/Structure';
import { TreePath } from '@interfaces/TreePath';
import { TreeNodeTotals } from '@models/TreeNodeTotals';
import { TreeNode } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { BaseService } from './base.service';
import { EstimateService } from './estimate.service';
import { OverBudgetResult } from './models/over-budget-result';

@Injectable({
  providedIn: 'root'
})
export class EstimateBudgetService extends BaseService {
  public constructor(
    private http: HttpClient,
    private estimateService: EstimateService
  ) {
    super();
  }

  private getEstimateBudgetValuesPromise?: Promise<BudgetValueDto[]>;
  private getDepartmentBudgetsGpPromise?: Promise<BudgetDto[]>;
  private projectCode?: string;
  public hasApprovedEstimate = true;
  private hasApprovedEstimatePromise: Promise<boolean>;

  /**
   * From an array of budgets. Find the appropriate budget based on
   * the specified treepath.
   * @param budgetValues Array of all budgets
   * @param treePath path from level1 down to department or category code to find the correct budget.
   */
  private getBudget<T extends BudgetValueDto | BudgetDto>(budgetValues: T[], treePath: TreePath) {
    const budget = budgetValues.find(
      b =>
        b.level1Code === treePath.level1Code &&
        b.level2Code === treePath.level2Code &&
        b.departmentCode === treePath.departmentCode &&
        (b.categoryCode === treePath.categoryCode || treePath.categoryCode === undefined)
    );
    return budget;
  }

  /**
   * Small helper method that checks if the specified value
   * is over the specified budget. Budgets are verified against
   * negative values so we are using -1 to invert the value back
   * to positive.
   *
   * Rounding budget to 2 Decimals because TreeNodeTotals.ts values are rounded to 2 decimals.
   */
  private isValueOverBudget(value: number, budget: number): boolean {
    return -1 * value > (budget ? MathHelper.round2Decimals(Utils.positiveValueOrZero(budget)) : 0);
  }

  /**
   * Method that will check for each class if we're removing
   * more hours than previously assigned to it (budget).
   *
   * @param treeNodeTotals totals
   * @param classes classes of the estimate used to link to classId
   * @param treePath path to the current element
   * @param budgetValue the budget to compare against
   */
  private checkForClassHoursOverBudget(
    treeNodeTotals: TreeNodeTotals,
    classes: ClassificationEstimate[],
    treePath: TreePath,
    budgetValue: BudgetValueDto
  ) {
    const results: OverBudgetResult[] = [];

    Object.entries(treeNodeTotals.classificationTotals).forEach(([key, value]) => {
      const classEstimate = classes.find(c => c.id === key);

      if (!classEstimate) {
        return;
      }
      const classId = classEstimate.classificationId;

      if (!this.isValueOverBudget(value, budgetValue.hourlyClassBudget[classId])) {
        return;
      }

      results.push({
        result: OverBudgetError.HourlyBudget,
        source: treePath,
        errorMessage: 'ERROR.OVERBUDGET.CLASS_BUDGET',
        errorMessageParams: {
          className: classEstimate.classification?.code,
          budgetHours: `${budgetValue.hourlyClassBudget[classId]?.toFixed(2) || 0}`,
          classHours: `${(-1 * value).toFixed(2)}`
        }
      });
    });
    return results;
  }

  /**
   * Method that will check for each internal resource if we're removing
   * more quantity than previously assigned to it (budget).
   *
   * @param treeNodeTotals totals
   * @param internalResources internal resources of the estimate used to link to internalResourceId
   * @param treePath path to the current element
   * @param budgetValue the budget to compare against
   */
  private checkForInternalResourcesOverBudget(
    treeNodeTotals: TreeNodeTotals,
    internalResourceEstimates: InternalResource_Estimate[],
    treePath: TreePath,
    budgetValue: BudgetValueDto
  ) {
    const results: OverBudgetResult[] = [];
    Object.entries(treeNodeTotals.internalResourceTotals).forEach(([key, value]) => {
      const internalResourceEstimate = internalResourceEstimates.find(c => c.id === key);
      if (internalResourceEstimate) {
        const intResourceId = internalResourceEstimate.internalResourceId;
        if (this.isValueOverBudget(value, budgetValue.internalResourceBudget[intResourceId])) {
          const internalResourceCode = internalResourceEstimate.internalResource?.code;
          results.push({
            result: OverBudgetError.InternalResourceBudget,
            source: treePath,
            errorMessage: 'ERROR.OVERBUDGET.INTERNAL_RESOURCE_BUDGET',
            errorMessageParams: {
              resourceName: internalResourceCode,
              budgetQuantity: `${budgetValue.internalResourceBudget[intResourceId]?.toFixed(2) || 0}`,
              resourceQuantity: `${(-1 * value).toFixed(2)}`
            }
          });
        }
      }
    });
    return results;
  }

  /**
   * Generic method to append an error object
   * to the results if we're removing
   * more hours (value) than previously assigned to it (budget).
   *
   * @param value the value inputted by the user
   * @param budget the budget value
   * @param error the error type of overbudget
   * @param errorMessage the i18n key to the error message
   * @param treePath path to the current element
   */
  private checkforOverBudget(
    value: number,
    budget: number,
    error: OverBudgetError,
    errorMessage: string,
    treePath: TreePath
  ) {
    if (this.isValueOverBudget(value, budget)) {
      return {
        result: error,
        source: treePath,
        errorMessage: errorMessage,
        errorMessageParams: {
          value: `${(-1 * value).toFixed(2)}`,
          budget: `${budget.toFixed(2)}`
        }
      };
    }
    return { result: OverBudgetError.None };
  }

  /**
   * Method that will check for any overbudget
   * field in the node. If more validation needs to be done
   * here is where to add some.
   *
   * @param budgetValue the budget to compare against
   * @param treeNodeTotals computed totals of inputted values
   * @param classes classes of the estimate used to link to classId
   * @param treePath path to the current element. Useful for display.
   */
  private calcIsOverBudget(
    budgetValue: BudgetValueDto,
    treeNodeTotals: TreeNodeTotals,
    classes: ClassificationEstimate[],
    internalResources: InternalResource_Estimate[],
    treePath: TreePath
  ): OverBudgetResult[] {
    let result: OverBudgetResult[] = [];

    result = result.concat(
      this.checkForClassHoursOverBudget(treeNodeTotals, classes, treePath, budgetValue),
      this.checkForInternalResourcesOverBudget(treeNodeTotals, internalResources, treePath, budgetValue)
    );

    result.push(
      this.checkforOverBudget(
        treeNodeTotals.totalHoursBillableFinal || treeNodeTotals.totalHoursBillable,
        budgetValue.billableFeesBudget ?? 0,
        OverBudgetError.BillableFeesBudget,
        'ERROR.OVERBUDGET.BILLABLE_FEES_BUDGET',
        treePath
      )
    );

    result.push(
      this.checkforOverBudget(
        treeNodeTotals.totalInternalFeesBillableFinal || treeNodeTotals.totalInternalFeesBillable,
        budgetValue.billableInternalFeesBudget ?? 0,
        OverBudgetError.BillableInternalFeesBudget,
        'ERROR.OVERBUDGET.BILLABLE_INTERNAL_FEES_BUDGET',
        treePath
      )
    );

    result.push(
      this.checkforOverBudget(
        treeNodeTotals.totalHoursCost,
        budgetValue.costFeesBudget ?? 0,
        OverBudgetError.CostFeesBudget,
        'ERROR.OVERBUDGET.COST_FEES_BUDGET',
        treePath
      )
    );

    result.push(
      this.checkforOverBudget(
        treeNodeTotals.totalInternalFeesCost,
        budgetValue.costInternalFeesBudget ?? 0,
        OverBudgetError.CostInternalFeesBudget,
        'ERROR.OVERBUDGET.COST_INTERNAL_FEES_BUDGET',
        treePath
      )
    );

    result.push(
      this.checkforOverBudget(
        treeNodeTotals.totalExpensesCost,
        budgetValue.costExpenseBudget ?? 0,
        OverBudgetError.CostExpenseBudget,
        'ERROR.OVERBUDGET.COST_EXPENSE_BUDGET',
        treePath
      )
    );

    result.push(
      this.checkforOverBudget(
        treeNodeTotals.totalExpensesBillableFinal || treeNodeTotals.totalExpensesBillable,
        budgetValue.billableExpenseBudget ?? 0,
        OverBudgetError.BillableExpenseBudget,
        'ERROR.OVERBUDGET.BILLABLE_EXPENSE_BUDGET',
        treePath
      )
    );

    return result.filter(r => r.result !== OverBudgetError.None);
  }

  /**
   * Method that will check for any overbudget
   * field in the node. If more validation needs to be done
   * here is where to add some.
   *
   * @param budgetValue the budget to compare against
   * @param treeNodeTotals computed totals of inputted values
   * @param classes classes of the estimate used to link to classId
   * @param treePath path to the current element. Useful for display.
   */
  private calcIsOverBudget_Sifia(budgetValue: BudgetDto, treeNodeTotals: TreeNodeTotals, treePath: TreePath) {
    const result: OverBudgetResult[] = [];

    result.push(
      this.checkforOverBudget(
        treeNodeTotals.totalHours,
        budgetValue.hoursBudget ?? 0,
        OverBudgetError.HourlyBudget,
        'ERROR.OVERBUDGET.LEVEL_HOURS_BUDGET',
        treePath
      )
    );

    result.push(
      this.checkforOverBudget(
        treeNodeTotals.totalHoursBillableFinal || treeNodeTotals.totalHoursBillable,
        budgetValue.billableBudget ?? 0,
        OverBudgetError.BillableFeesBudget,
        'ERROR.OVERBUDGET.BILLABLE_FEES_BUDGET',
        treePath
      )
    );

    const internalFeesBillable =
      treeNodeTotals.totalInternalFeesBillableFinal || treeNodeTotals.totalInternalFeesBillable;
    const expensesBillable = treeNodeTotals.totalExpensesBillableFinal || treeNodeTotals.totalExpensesBillable;

    result.push(
      this.checkforOverBudget(
        internalFeesBillable + expensesBillable,
        budgetValue.billableExpenseBudget ?? 0,
        OverBudgetError.BillableInternalFeesBudget,
        'ERROR.OVERBUDGET.BILLABLE_EXPENSE_BUDGET',
        treePath
      )
    );

    const costBudget =
      treeNodeTotals.totalInternalFeesCost + treeNodeTotals.totalExpensesCost + treeNodeTotals.totalHoursCost;

    result.push(
      this.checkforOverBudget(
        costBudget,
        budgetValue.costBudget ?? 0,
        OverBudgetError.CostFeesBudget,
        'ERROR.OVERBUDGET.COST_BUDGET',
        treePath
      )
    );

    return result.filter(r => r.result !== OverBudgetError.None);
  }

  /**
   * We need to verify only in reallocation and
   * additionalService as we're comparing against
   * previously created estimates for this project.
   *
   * Negative values don't make sense in a brand new
   * estimates.
   */
  public needToVerifyBudget(estimate: Estimate) {
    return (
      estimate.status === StatusEnum.New &&
      (estimate.estimateType === EstimateTypeEnum.Reallocation ||
        estimate.estimateType === EstimateTypeEnum.AdditionalService)
    );
  }

  /**
   * Backend query for budget values
   */

  private async getEstimateBudgetValues(projectCode: string) {
    if (this.getEstimateBudgetValuesPromise === undefined) {
      this.getEstimateBudgetValuesPromise = lastValueFrom(
        this.http.get<BudgetValueDto[]>(`${this.baseUrl}/web/Budget/EstimateBudgetValues?projectCode=${projectCode}`)
      );
    }
    const budgetValues = await this.getEstimateBudgetValuesPromise;
    return budgetValues ?? [];
  }

  private async getDepartmentBudgetsGp(projectCode: string) {
    if (this.getDepartmentBudgetsGpPromise === undefined) {
      this.getDepartmentBudgetsGpPromise = lastValueFrom(
        this.http.get<BudgetDto[]>(`${this.baseUrl}/web/Budget/DepartmentBudgetsGp?projectCode=${projectCode}`)
      );
    }
    const departmentBudgetValues = await this.getDepartmentBudgetsGpPromise;
    return departmentBudgetValues ?? [];
  }

  public async verifyIsOverBudget(
    estimate: Estimate,
    startingNode: TreeNode<TreeNodeData<Structure | Estimate | EstimateLine>>,
    groupBudgetsByProjDept: boolean
  ) {
    if (this.projectCode !== estimate.projectCode) {
      this.hasApprovedEstimatePromise = this.estimateService.hasApprovedNewEstimate(estimate.projectCode);
      this.projectCode = estimate.projectCode;
      this.getEstimateBudgetValuesPromise = undefined;
    }
    this.hasApprovedEstimate = await this.hasApprovedEstimatePromise;

    if (!this.hasApprovedEstimate || estimate.forceBudgetValidationGp) {
      const departmentBudgetValues = await this.getDepartmentBudgetsGp(estimate.projectCode);
      return this.recursiveIsOverBudgetGp(
        estimate,
        startingNode,
        departmentBudgetValues,
        groupBudgetsByProjDept ? estimate.departmentCode : undefined
      );
    }

    const budgetValues = await this.getEstimateBudgetValues(estimate.projectCode);
    return this.recursiveIsOverBudget(estimate, startingNode, budgetValues);
  }

  /**
   * Will run for every level of the treetable.
   * the recursivetree will apply its function to
   * leaf nodes only as they are the only possible input
   * in the grid.
   * @param startingNode any node level of the treetable
   * @param estimateBudgetValues array of budgets collected from previous approved estimates.
   */
  private recursiveIsOverBudget(
    estimate: Estimate,
    startingNode: TreeNode<TreeNodeData<Structure | Estimate | EstimateLine>>,
    estimateBudgetValues: BudgetValueDto[]
  ) {
    let result: OverBudgetResult[] = [];

    if (!this.needToVerifyBudget(estimate)) {
      return result;
    }

    TreeNodeHelper.recurseTree(startingNode, node => {
      //check if we could be an editable leaf.
      if (
        ![
          TreeNodeType.Department,
          TreeNodeType.GenericExpense,
          TreeNodeType.GenericTime,
          TreeNodeType.GenericInternalFee,
          TreeNodeType.Category
        ].includes(node.data.type)
      ) {
        return;
      }

      //we're in category mode. We always take data from leafs.
      if (node.data.type === TreeNodeType.Department && node.children.length > 0) {
        return;
      }

      const typedNode = node.data.entity as EstimateLine;
      const treePath: TreePath = {
        level1Code: typedNode.structure.level1?.code ?? '',
        level2Code: typedNode.structure.level2?.code ?? '',
        departmentCode: typedNode.departmentEstimate.department.code,
        categoryCode: typedNode.category?.code
      };
      const classes: ClassificationEstimate[] = typedNode.classificationLines.map(cl => cl.classificationEstimate);
      const internalResources: InternalResource_Estimate[] = typedNode.internalFeeLines.map(
        il => il.internalResourceEstimate
      );

      const budgetValue = this.getBudget(estimateBudgetValues, treePath) ?? {
        hourlyClassBudget: {},
        costFeesBudget: 0,
        billableFeesBudget: 0,
        internalResourceBudget: {},
        costInternalFeesBudget: 0,
        billableInternalFeesBudget: 0,
        costExpenseBudget: 0,
        billableExpenseBudget: 0,
        projectCode: undefined,
        level1Code: treePath.level1Code,
        level2Code: treePath.level2Code,
        departmentCode: treePath.departmentCode,
        categoryCode: treePath.categoryCode
      };

      result = result.concat(
        this.calcIsOverBudget(budgetValue, node.data.totals, classes, internalResources, treePath)
      );
    });
    return result;
  }

  /**
   * Will run for every level of the treetable.
   * the recursivetree will apply its function to
   * leaf nodes only as they are the only possible input
   * in the grid.
   * @param startingNode any node level of the treetable
   * @param estimateBudgetValues array of budgets collected from previous approved estimates.
   */
  private recursiveIsOverBudgetGp(
    estimate: Estimate,
    startingNode: TreeNode<TreeNodeData<Structure | Estimate | EstimateLine>>,
    sifiaBudgetValues: BudgetDto[],
    limitToDepartmentCode: string | undefined
  ): OverBudgetResult[] {
    let result: OverBudgetResult[] = [];

    if (!this.needToVerifyBudget(estimate)) {
      return result;
    }

    TreeNodeHelper.recurseTree(startingNode, node => {
      let treePath: TreePath;

      if (limitToDepartmentCode) {
        if (TreeNodeType.Level2 !== node.data.type) {
          return;
        }

        const structure = node.data.entity as Structure;
        treePath = {
          level1Code: structure.level1.code,
          level2Code: structure.level2.code,
          departmentCode: limitToDepartmentCode,
          categoryCode: undefined
        };
      } else {
        if (TreeNodeType.Department !== node.data.type) {
          return;
        }

        const estimateLine = node.data.entity as EstimateLine;
        treePath = {
          level1Code: estimateLine.structure.level1.code,
          level2Code: estimateLine.structure.level2.code,
          departmentCode: estimateLine.departmentEstimate.department.code,
          categoryCode: undefined
        };
      }

      const budgetValue = this.getBudget(sifiaBudgetValues, treePath) ?? {
        ...treePath,
        projectCode: estimate.projectCode,
        billableBudget: 0,
        billableExpenseBudget: 0,
        costBudget: 0,
        hoursBudget: 0
      };

      result = result.concat(this.calcIsOverBudget_Sifia(budgetValue, node.data.totals, treePath));
    });
    return result;
  }
}
